/**
 * 都市商务模板 - 登录页
 *
 * 设计核心理念：左深色品牌区 + 右白色表单区的 45/55 分栏
 * 与 sunset（暖色渐变 + 右侧浮窗卡片）完全不同的排版骨架：
 * - 左栏：深藏青渐变品牌区（logo + 标语 + 特性 + 版权）
 * - 右栏：纯白背景居中表单（无边框卡片，简洁商务感）
 */

import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useAuth } from "@/hooks/useAuth";

export default function UrbanLoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { siteName: contextSiteName } = useSite();
  const { siteConfig } = useSiteConfig();

  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const siteName = contextSiteName || siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!formData.username.trim() || !formData.password.trim()) {
      setError("请填写用户名和密码");
      return;
    }
    setSubmitting(true);
    try {
      await login(formData.username, formData.password);
      navigate("/");
    } catch (err: any) {
      setError(err?.message || "登录失败，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── 左侧：深色品牌区 ── */}
      <div className="hidden lg:flex w-[45%] relative flex-col justify-between p-12 bg-gradient-to-br from-[#16283F] via-[#1B3A5C] to-[#2C5A8C] overflow-hidden">
        {/* 装饰几何 */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F08A24]/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 w-40 h-40 rounded-full border border-white/10" />
        <div className="absolute top-[38%] left-[42%] w-72 h-72 rounded-full border border-white/5" />

        {/* Logo + 品牌 */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-10">
            {logo ? (
              <img src={logo} alt={siteName} className="h-10 w-auto" />
            ) : (
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#F08A24] to-[#E26A1B] flex items-center justify-center text-white text-xl font-black shadow-lg shadow-orange-500/30">
                {siteName ? siteName.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
            {siteName && <span className="text-xl font-bold text-white">{siteName}</span>}
          </div>

          <div className="space-y-2 mb-10">
            <h1 className="text-3xl font-extrabold text-white leading-tight">
              欢迎回来
            </h1>
            <p className="text-gray-300 leading-relaxed text-sm">
              登录您的账户，管理发布的信息，享受更多会员服务
            </p>
          </div>

          {/* 特性展示 */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <i className="ri-file-list-line text-[#F5A94D]" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">发布管理</div>
                <div className="text-xs text-gray-400">添加、编辑您的分类信息</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <i className="ri-vip-crown-line text-[#F5A94D]" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">会员特权</div>
                <div className="text-xs text-gray-400">享受置顶、加V等专属服务</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-[#F5A94D]" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">安全可靠</div>
                <div className="text-xs text-gray-400">平台信息多重审核保障</div>
              </div>
            </div>
          </div>
        </div>

        {/* 底部版权 */}
        <p className="relative z-10 text-xs text-gray-400">
          © {new Date().getFullYear()} {siteName || 'urban'} · 信息服务平台
        </p>
      </div>

      {/* ── 右侧：白色表单区 ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-[#F4F6F8]">
        <div className="w-full max-w-md">
          {/* 移动端 Logo */}
          <div className="lg:hidden mb-8 text-center">
            {logo ? (
              <img src={logo} alt="" className="h-10 mx-auto mb-2" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#F08A24] to-[#E26A1B] mx-auto flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-orange-500/20">
                {siteName ? siteName.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
            {siteName && <p className="text-sm text-gray-500 mt-2">{siteName}</p>}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 sm:p-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-1">登录账户</h2>
            <p className="text-sm text-gray-400 mb-8">
              还没有账号？{" "}
              <Link to="/register" className="text-[#1B3A5C] hover:text-[#F08A24] font-semibold transition-colors">
                立即注册
              </Link>
            </p>

            {error && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line" />{error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">用户名 / 手机号</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-user-line text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="请输入用户名"
                    className="w-full pl-11 pr-4 py-3.5 bg-[#F4F6F8] border border-transparent rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#1B3A5C]/30 focus:ring-4 focus:ring-[#1B3A5C]/5 transition-all text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">密码</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="请输入密码"
                    className="w-full pl-11 pr-4 py-3.5 bg-[#F4F6F8] border border-transparent rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#1B3A5C]/30 focus:ring-4 focus:ring-[#1B3A5C]/5 transition-all text-sm"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-gray-500 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded accent-[#1B3A5C]" />
                  记住我
                </label>
                <Link to="/forgot-password" className="text-[#1B3A5C] hover:text-[#F08A24] font-medium transition-colors">
                  忘记密码？
                </Link>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white font-bold rounded-lg hover:from-[#E26A1B] hover:to-[#C95A12] transition-all shadow-lg shadow-orange-500/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />登录中...
                  </span>
                ) : "立即登录"}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-gray-400 hover:text-gray-500 inline-flex items-center gap-1 transition-colors">
                <i className="ri-arrow-left-line" />返回首页
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
